import type { I18nKey, TParams } from '../../i18n/index.ts';
import type { ChipTone } from '../../ui/Chip.tsx';
import type { LampTone } from '../../ui/StatusLamp.tsx';

/**
 * Known statuspage words. `I18nKey` is a static union, so
 * `` t(`status.official.${group}.${value}`) `` cannot typecheck. A value
 * missing from these maps is the vendor's own word and renders raw.
 */
type Translate = (key: I18nKey, params?: TParams) => string;

const INDICATOR_LABELS: Readonly<Record<string, I18nKey>> = {
  none: 'status.official.indicator.none',
  minor: 'status.official.indicator.minor',
  major: 'status.official.indicator.major',
  critical: 'status.official.indicator.critical',
  maintenance: 'status.official.indicator.maintenance',
  unknown: 'status.official.indicator.unknown',
};

const COMPONENT_LABELS: Readonly<Record<string, I18nKey>> = {
  operational: 'status.official.component.operational',
  degraded_performance: 'status.official.component.degraded_performance',
  partial_outage: 'status.official.component.partial_outage',
  major_outage: 'status.official.component.major_outage',
  under_maintenance: 'status.official.component.under_maintenance',
};

const INCIDENT_LABELS: Readonly<Record<string, I18nKey>> = {
  investigating: 'status.official.incident.investigating',
  identified: 'status.official.incident.identified',
  monitoring: 'status.official.incident.monitoring',
  resolved: 'status.official.incident.resolved',
  postmortem: 'status.official.incident.postmortem',
};

const MAINTENANCE_LABELS: Readonly<Record<string, I18nKey>> = {
  scheduled: 'status.official.maintenance.scheduled',
  in_progress: 'status.official.maintenance.in_progress',
  verifying: 'status.official.maintenance.verifying',
  completed: 'status.official.maintenance.completed',
};

const IMPACT_LABELS: Readonly<Record<string, I18nKey>> = {
  none: 'status.official.impact.none',
  minor: 'status.official.impact.minor',
  major: 'status.official.impact.major',
  critical: 'status.official.impact.critical',
};

export type OfficialLabelGroup = 'indicator' | 'component' | 'incident' | 'maintenance' | 'impact';

function labelsFor(group: OfficialLabelGroup): Readonly<Record<string, I18nKey>> {
  switch (group) {
    case 'indicator':
      return INDICATOR_LABELS;
    case 'component':
      return COMPONENT_LABELS;
    case 'incident':
      return INCIDENT_LABELS;
    case 'maintenance':
      return MAINTENANCE_LABELS;
    case 'impact':
      return IMPACT_LABELS;
  }
}

/**
 * Translate a known vendor word. Any other string is returned unchanged
 * so a value the status page invents tomorrow still renders.
 */
export function officialStatusLabel(
  group: OfficialLabelGroup,
  value: string,
  t: Translate,
): string {
  const labels = labelsFor(group);
  // `labels[value]` would also see inherited keys (`toString`); only own
  // entries are translations.
  if (!Object.hasOwn(labels, value)) {
    return value;
  }
  const key = labels[value];
  return key === undefined ? value : t(key);
}

/** Page indicator → lamp. Unknown words share the muted `off` tone. */
export function indicatorTone(indicator: string): LampTone {
  switch (indicator) {
    case 'none':
      return 'ok';
    case 'minor':
    case 'maintenance':
      return 'warn';
    case 'major':
    case 'critical':
      return 'bad';
    default:
      return 'off';
  }
}

export function componentTone(status: string): ChipTone {
  switch (status) {
    case 'operational':
      return 'ok';
    case 'degraded_performance':
    case 'partial_outage':
    case 'under_maintenance':
      return 'warn';
    case 'major_outage':
      return 'bad';
    default:
      return 'neutral';
  }
}

export function incidentTone(status: string): ChipTone {
  switch (status) {
    case 'investigating':
    case 'identified':
    case 'monitoring':
      return 'warn';
    case 'resolved':
      return 'ok';
    case 'postmortem':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function maintenanceTone(status: string): ChipTone {
  switch (status) {
    case 'in_progress':
    case 'verifying':
      return 'warn';
    case 'completed':
      return 'ok';
    case 'scheduled':
      return 'neutral';
    default:
      return 'neutral';
  }
}
